import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { setMachineContext } from "../../../shared/chatBot/chatBotSlice";
import { getMachines } from "../../machinery/customerMachinerySlice";
import { Machine } from "../../machinery/models/models";

// ===================================================================
//  M7-B2: Read machineId from URL params (QR kiosk integration)
//
//  When the user navigates to /midas?machineId={guid}, this hook:
//  1. Ensures the fleet is loaded
//  2. Tries to find the machine in the user's fleet
//  3. Sets machine context (with full display data if in fleet,
//     or machineId-only if not — bot API returns model/serial)
//  4. Cleans the URL via replaceState
// ===================================================================

const useMidasUrlMachineContext = (): void => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const handled = useRef(false);

  const machinesMap = useAppSelector(
    (state) => state.customerMachinery.machines
  );
  const machineryLoading = useAppSelector(
    (state) => state.customerMachinery.loading
  );

  // Ensure fleet is loaded when machineId param is present
  useEffect(() => {
    const machineIdParam = searchParams.get("machineId");
    if (!machineIdParam || handled.current) return;

    const hasFleet = machinesMap && Object.keys(machinesMap).length > 0;
    if (!hasFleet && !machineryLoading) {
      dispatch(getMachines({}));
    }
  }, [searchParams, machinesMap, machineryLoading, dispatch]);

  // Set machine context once fleet data is available (or loading finishes)
  useEffect(() => {
    const machineIdParam = searchParams.get("machineId");
    if (!machineIdParam || handled.current) return;
    if (machineryLoading) return;

    handled.current = true;

    // Try to find machine in user's fleet
    let foundMachine: Machine | undefined;
    if (machinesMap && typeof machinesMap === "object") {
      for (const group of Object.values(machinesMap)) {
        if (Array.isArray(group)) {
          foundMachine = group.find((m) => m.id === machineIdParam);
          if (foundMachine) break;
        }
      }
    }

    if (foundMachine) {
      // Machine is in user's fleet — use full display data
      dispatch(
        setMachineContext({
          machineId: foundMachine.id,
          displayData: {
            model: foundMachine.model,
            serialNumber: foundMachine.serialNumber,
            internalNumber: foundMachine.internalNumber,
            system: foundMachine.system,
            manufacturerName:
              foundMachine.customBrandName ||
              foundMachine.manufacturer?.name ||
              "",
            typeName: foundMachine.type?.name,
            axisConfigName: foundMachine.axisConfiguration?.name,
            controlSystemBrandName: foundMachine.controlSystemBrand?.name,
            imageContent: foundMachine.image?.content,
            description: foundMachine.description,
            productionYear: foundMachine.productionYear,
          },
        })
      );
    } else {
      // Machine not in fleet (e.g. QR scan of someone else's machine)
      // Set context with machineId only; the bot API will return model/serial
      dispatch(
        setMachineContext({
          machineId: machineIdParam,
          displayData: {},
        })
      );
    }

    // Clean the URL (remove query params without triggering navigation).
    // This hook is used only by MidasPage (V1), which renders at /midas-v1.
    window.history.replaceState({}, "", "/midas-v1");
  }, [searchParams, machinesMap, machineryLoading, dispatch]);
};

export default useMidasUrlMachineContext;
